import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Lightning, Database, Cloud, DeviceMobile } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { TECH_STACK } from '../data/siteData';

const CATEGORY_ICONS = {
  frontend: Code,
  backend: Lightning,
  mobile: DeviceMobile,
  database: Database,
  cloud: Cloud,
};

const CATEGORY_COLORS = {
  frontend: '#00F0FF',
  backend: '#7000FF',
  mobile: '#00FF94',
  database: '#FFD600',
  cloud: '#FF6B00',
};

export default function TechnologyPage() {
  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00F0FF]/5 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono uppercase tracking-widest text-[#00F0FF] mb-4 block">Tech Excellence</span>
            <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tight mb-6">
              Our Technology Stack
            </h1>
            <p className="text-xl theme-text-secondary max-w-2xl mx-auto">
              Full-stack expertise across 50+ technologies. We choose the right tools for your specific needs.
            </p>
          </motion.div>

          {/* Tech Categories */}
          {Object.entries(TECH_STACK).map(([category, techs], catIndex) => {
            const Icon = CATEGORY_ICONS[category] || Code;
            const color = CATEGORY_COLORS[category] || '#00F0FF';
            
            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
                className="mb-16"
              >
                <div className="flex items-center gap-4 mb-8">
                  <div 
                    className="w-12 h-12 flex items-center justify-center"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon size={28} style={{ color }} />
                  </div>
                  <h2 className="font-heading text-2xl font-bold capitalize">{category}</h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {techs.map((tech, i) => (
                    <div 
                      key={tech.id}
                      className="group border theme-border theme-surface-el p-6 hover:border-[#00F0FF]/50 transition-all"
                    >
                      <h3 className="font-bold text-lg mb-2 group-hover:text-[#00F0FF] transition-colors">
                        {tech.name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm">
                        <span 
                          className="px-2 py-1 text-xs"
                          style={{ backgroundColor: `${color}20`, color }}
                        >
                          {tech.expertise}
                        </span>
                        <span className="theme-text-muted">{tech.years}+ years</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Hire by Tech - SEO Links */}
      <section className="py-24 theme-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">Hire Developers by Technology</h2>
            <p className="theme-text-muted">Find the right expertise for your project</p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { tech: 'React', location: 'USA' },
              { tech: 'Python', location: 'USA' },
              { tech: 'Node.js', location: 'UK' },
              { tech: 'Flutter', location: 'Australia' },
              { tech: 'React Native', location: 'Europe' },
              { tech: 'AWS', location: 'USA' },
              { tech: 'Java', location: 'UK' },
              { tech: 'AI/ML', location: 'USA' },
            ].map((item, i) => (
              <Link
                key={i}
                to={`/hire-${item.tech.toLowerCase().replace(/[^a-z]/g, '-')}-developers-${item.location.toLowerCase()}`}
                className="p-4 border theme-border theme-surface-el hover:border-[#00F0FF]/50 transition-all group"
              >
                <span className="text-sm theme-text-secondary group-hover:text-[#00F0FF]">
                  Hire {item.tech} Developers - {item.location}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">Need Help Choosing?</h2>
          <p className="text-xl theme-text-secondary mb-10">
            Our AI will recommend the optimal tech stack for your project.
          </p>
          <Link to="/start-project">
            <Button className="bg-[#00F0FF] text-black font-bold h-14 px-8 uppercase">
              Get Tech Recommendation <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
